import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab } from '@mui/material';
import { Code } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function AngularWorkspace({ question, onCodeChange, initialCode = '' }) {
  const defaultCode = `import { Component } from '@angular/core';

@Component({
  selector: 'app-root',
  template: \`
    <div class="app">
      <h1>Angular Component</h1>
      <p>Count: {{ count }}</p>
      <button (click)="increment()">Increment</button>
    </div>
  \`,
  styles: [\`
    .app {
      padding: 20px;
      font-family: Arial, sans-serif;
    }
    button {
      padding: 10px 20px;
      font-size: 16px;
      cursor: pointer;
    }
  \`]
})
export class AppComponent {
  count = 0;

  increment() {
    this.count++;
  }
}`;

  const [code, setCode] = useState(initialCode || defaultCode);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'Angular Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Angular Workspace</strong> - Angular requires a full development environment.
        For this interview, write your TypeScript component code. Full compilation and preview
        require Angular CLI which is not available in the browser.
      </Alert>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Component Code" />
        <Tab label="Instructions" />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="typescript"
            path="app.component.ts"
            value={code}
            onChange={(value) => setCode(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {activeTab === 1 && (
        <Paper sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Angular Development Guidelines
          </Typography>
          <Typography variant="body2" paragraph>
            Write your Angular component using TypeScript. Include:
          </Typography>
          <ul>
            <li>Component decorator with selector, template, and styles</li>
            <li>Class definition with properties and methods</li>
            <li>Proper TypeScript typing</li>
            <li>Angular template syntax (interpolation, event binding, directives)</li>
          </ul>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            Your code will be evaluated for:
          </Typography>
          <ul>
            <li>Correct Angular syntax and best practices</li>
            <li>Proper component structure</li>
            <li>TypeScript usage and type safety</li>
            <li>Template and data binding implementation</li>
          </ul>
        </Paper>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Code />}
          onClick={() => setActiveTab(activeTab === 0 ? 1 : 0)}
        >
          {activeTab === 0 ? 'View Instructions' : 'Back to Code'}
        </Button>
      </Box>
    </Box>
  );
}

export default AngularWorkspace;
